import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Send, Clock, Play, Square, Plus, GitMerge, X, Pencil, Check, Trash2, Link2 } from 'lucide-react';
import { useApi } from '../auth/useApi';
import { useImagePaste, makeFetchUploader } from '../hooks/useImagePaste';
import { UserAccessPopover } from '../components/UserAccessPopover';
import { DescriptionBody } from '../components/DescriptionBody';
import { TicketAttachments } from '../components/TicketAttachments';
import { SearchableSelect } from '../components/SearchableSelect';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#c4533a',
  High: '#c4793a',
  Medium: '#7a9b7e',
  Low: '#a8a8a8',
};

interface Me {
  userId: number;
  email: string;
  displayName: string;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface TicketDetail {
  TicketId: number;
  TicketKey?: string;
  Subject: string;
  Description: string;
  Priority: string;
  StatusId: number;
  StatusName: string;
  CategoryName: string | null;
  CategoryId: number | null;
  CreatedByName: string;
  CreatedByUserId: number;
  AssignedToName: string | null;
  AssignedToUserId: number | null;
  SecondaryAssignees?: { UserId: number; DisplayName: string }[];
  CreatedAt: string;
  UpdatedAt: string;
  ResolvedAt: string | null;
  FirstResponseAt: string | null;
  Resolution: string | null;
  TotalTimeMinutes: number;
  ActiveTimerStartedAt: string | null;
  MergedIntoTicketId: number | null;
  MergedIntoTicketKey: string | null;
}

interface MergedChild {
  TicketId: number;
  TicketKey: string;
  Subject: string;
}


interface Comment {
  CommentId: number;
  TicketId: number;
  AuthorUserId: number;
  AuthorName: string;
  Body: string;
  IsInternal: boolean;
  CreatedAt: string;
  EditedAt?: string | null;
  IsDeleted?: boolean;
  MinutesLogged?: number | null;
}

interface Status {
  StatusId: number;
  Name: string;
  IsTerminal: boolean;
}

interface User {
  UserId: number;
  DisplayName: string;
  Email: string;
  Role: string;
}

interface SLA {
  responseDueAt: string | null;
  resolveDueAt: string | null;
  responseElapsedPct: number;
  resolveElapsedPct: number;
  responseDone?: boolean;
  resolveDone?: boolean;
  responseMet?: boolean | null;
  resolveMet?: boolean | null;
}

interface TicketResponse {
  ticket: TicketDetail;
  comments: Comment[];
  tags: string[];
  sla?: SLA;
  mergedChildren?: MergedChild[];
  survey?: Survey | null;
  checklist?: ChecklistItem[];
}

interface ChecklistItem {
  ItemId: number;
  Text: string;
  IsDone: boolean;
  SortOrder: number;
  CheckedByUserId: number | null;
  CheckedByName: string | null;
  CheckedAt: string | null;
}

interface Survey {
  SurveyId: number;
  Rating: number | null;
  Comment: string | null;
  SentAt: string | null;
  RespondedAt: string | null;
}

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString('en', { dateStyle: 'medium', timeStyle: 'short' });
};

const Badge: React.FC<{ children: React.ReactNode; color: string }> = ({ children, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    color, background: `${color}15`, border: `1px solid ${color}40`,
  }}>{children}</span>
);

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: 18, marginBottom: 16, ...style,
  }}>{children}</div>
);

const SLABar: React.FC<{ pct: number; label: string; done?: boolean; met?: boolean | null }> =
  ({ pct, label, done, met }) => {
  const clamped = Math.min(100, Math.max(0, pct));
  // Finished clocks show a verdict; live clocks keep the traffic-light ramp.
  const color = done
    ? (met ? C.ok : C.danger)
    : pct >= 100 ? C.danger : pct >= 80 ? C.warn : C.ok;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.textMuted, marginBottom: 3 }}>
        <span>{label}</span>
        {done ? (
          <span style={{ color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {met ? 'met' : `breached · ${Math.round(pct)}%`}
          </span>
        ) : (
          <span style={{ color }}>{Math.round(pct)}%</span>
        )}
      </div>
      <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${done && met ? 100 : clamped}%`, background: color, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
};

// Responsive helper: true on narrow (phone) viewports.
function useIsNarrow(bp = 760): boolean {
  const [narrow, setNarrow] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= bp);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= bp);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bp]);
  return narrow;
}

export const TicketDetail: React.FC<{ me: Me }> = ({ me }) => {
  const { id } = useParams<{ id: string }>();
  const narrow = useIsNarrow();
  const api = useApi();

  const [data, setData] = useState<TicketResponse | null>(null);
  const [unmergeBusy, setUnmergeBusy] = useState<number | null>(null);
  const [unmergeStatus, setUnmergeStatus] = useState<Record<number, number>>({});
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingBusy, setRatingBusy] = useState(false);
  const [newChecklistText, setNewChecklistText] = useState('');
  const [checklistBusy, setChecklistBusy] = useState(false);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<{ CategoryId: number; Name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Comment composer
  const [newComment, setNewComment] = useState('');
  // Inline image paste in comments (reuses the same upload + SAS plumbing as the description).
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const uploadImage = useMemo(
    () => makeFetchUploader(api.apiBase, api.getAccessToken),
    [api.apiBase, api.getAccessToken],
  );
  const { onPaste: onCommentPaste, uploading: commentImgUploading, error: commentImgError } = useImagePaste({
    value: newComment, onChange: setNewComment, uploadImage, textareaRef: commentRef,
  });
  const [isInternal, setIsInternal] = useState(false);
  const [postingComment, setPostingComment] = useState(false);

  // Merge modal (admin/tech only)
  const [mergeModalOpen, setMergeModalOpen] = useState(false);

  // Inline title editing (admin/tech)
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const saveTitle = async () => {
    const next = titleDraft.trim();
    if (!next) { alert('Title cannot be empty.'); return; }
    if (next === data?.ticket?.Subject) { setEditingTitle(false); return; }
    setSavingTitle(true);
    try {
      await updateField({ Subject: next } as any);
      setEditingTitle(false);
    } catch (e: any) {
      alert(`Failed to update title: ${e?.message ?? 'unknown error'}`);
    } finally {
      setSavingTitle(false);
    }
  };

  // Inline description editing (admin/tech)
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const saveDesc = async () => {
    const next = descDraft.trim();
    if (!next) { alert('Description cannot be empty.'); return; }
    if (next === data?.ticket?.Description) { setEditingDesc(false); return; }
    setSavingDesc(true);
    try {
      await updateField({ Description: next } as any);
      setEditingDesc(false);
    } catch (e: any) {
      alert(`Failed to update description: ${e?.message ?? 'unknown error'}`);
    } finally {
      setSavingDesc(false);
    }
  };

  // Comment edit (author of that comment) / soft-delete (admin/tech)
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const startEditComment = (c: any) => { setEditingCommentId(c.CommentId); setCommentDraft(c.Body); };
  const saveEditComment = async (cid: number) => {
    const body = commentDraft.trim();
    if (!body) { alert('Comment cannot be empty.'); return; }
    setCommentBusy(true);
    try {
      await api.patch(`/api/tickets/${id}/comments/${cid}`, { body });
      setEditingCommentId(null); setCommentDraft('');
      await load();
    } catch (e: any) {
      alert(`Failed to edit comment: ${e?.message ?? 'error'}`);
    } finally { setCommentBusy(false); }
  };
  const deleteComment = async (cid: number) => {
    if (!window.confirm('Delete this comment? It will show as "[deleted]" but stays in the audit log.')) return;
    setCommentBusy(true);
    try {
      await api.del(`/api/tickets/${id}/comments/${cid}`);
      await load();
    } catch (e: any) {
      alert(`Failed to delete comment: ${e?.message ?? 'error'}`);
    } finally { setCommentBusy(false); }
  };

  // Linked devices (assets) — link / unlink
  const [allAssets, setAllAssets] = useState<any[]>([]);
  const [assetPickerId, setAssetPickerId] = useState<number | ''>('');
  const [assetBusy, setAssetBusy] = useState(false);
  const loadAssetsForPicker = async () => {
    if (allAssets.length) return;
    try { const a = await api.get<any[]>('/api/assets'); setAllAssets(Array.isArray(a) ? a : []); } catch { /* ignore */ }
  };
  const linkAsset = async () => {
    if (!assetPickerId) return;
    setAssetBusy(true);
    try { await api.post(`/api/tickets/${id}/assets`, { assetId: assetPickerId }); setAssetPickerId(''); await load(); }
    catch (e: any) { alert(`Failed to link device: ${e?.message ?? 'error'}`); }
    finally { setAssetBusy(false); }
  };
  const unlinkAsset = async (assetId: number) => {
    setAssetBusy(true);
    try { await api.del(`/api/tickets/${id}/assets/${assetId}`); await load(); }
    catch (e: any) { alert(`Failed to unlink device: ${e?.message ?? 'error'}`); }
    finally { setAssetBusy(false); }
  };

  // Linked KB articles (admin/tech only) — tag / untag. Works for public + internal.
  const [allKbArticles, setAllKbArticles] = useState<any[]>([]);
  const [kbPickerId, setKbPickerId] = useState<number | ''>('');
  const [kbBusy, setKbBusy] = useState(false);
  const loadKbForPicker = async () => {
    if (allKbArticles.length) return;
    try { const a = await api.get<any[]>('/api/kb/articles'); setAllKbArticles(Array.isArray(a) ? a : []); } catch { /* ignore */ }
  };
  const linkKb = async () => {
    if (!kbPickerId) return;
    setKbBusy(true);
    try { await api.post(`/api/tickets/${id}/kb`, { articleId: kbPickerId }); setKbPickerId(''); await load(); }
    catch (e: any) { alert(`Failed to tag article: ${e?.message ?? 'error'}`); }
    finally { setKbBusy(false); }
  };
  const unlinkKb = async (articleId: number) => {
    setKbBusy(true);
    try { await api.del(`/api/tickets/${id}/kb/${articleId}`); await load(); }
    catch (e: any) { alert(`Failed to untag article: ${e?.message ?? 'error'}`); }
    finally { setKbBusy(false); }
  };

  // Watchers (secondary contacts)
  const [watchers, setWatchers] = useState<{ WatcherId: number; UserId: number; DisplayName: string; Email: string }[]>([]);
  const [showAddWatcher, setShowAddWatcher] = useState(false);
  const [addWatcherUserId, setAddWatcherUserId] = useState<number | ''>('');

  const canEdit = me.role === 'admin' || me.role === 'tech' || me.role === 'agent';
  const canSeeInternal = me.role === 'admin' || me.role === 'tech' || me.role === 'viewer' || me.role === 'agent';

  const load = async () => {
    setLoading(true);
    try {
      const [d, s, u, w, cats] = await Promise.all([
        api.get<TicketResponse>(`/api/tickets/${id}`),
        canEdit ? api.get<Status[]>('/api/admin/statuses') : Promise.resolve([]),
        api.get<User[]>('/api/users').catch(() => []),  // needed for watcher picker too
        api.get<{ WatcherId: number; UserId: number; DisplayName: string; Email: string }[]>(`/api/tickets/${id}/watchers`).catch(() => []),
        api.get<{ CategoryId: number; Name: string }[]>('/api/categories').catch(() => []),
      ]);
      setData(d);
      setStatuses(Array.isArray(s) ? s : []);
      setUsers(Array.isArray(u) ? u : []);
      setWatchers(Array.isArray(w) ? w : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const doUnmerge = async (childId: number) => {
    const statusId = unmergeStatus[childId] ??
      (statuses.find(s => s.Name === 'Open') ?? statuses.find(s => !s.IsTerminal) ?? statuses[0])?.StatusId;
    if (!statusId) return;
    setUnmergeBusy(childId);
    try {
      await api.post(`/api/tickets/${childId}/unmerge`, { statusId });
      await load();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to unmerge');
    } finally { setUnmergeBusy(null); }
  };

  const submitRating = async () => {
    if (ratingValue < 1) return;
    setRatingBusy(true);
    try {
      await api.post(`/api/tickets/${id}/satisfaction`, { rating: ratingValue, comment: ratingComment.trim() || null });
      await load();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to submit rating');
    } finally { setRatingBusy(false); }
  };

  const addChecklistItem = async () => {
    const text = newChecklistText.trim();
    if (!text) return;
    setChecklistBusy(true);
    try {
      const res = await api.post<{ itemId: number }>(`/api/tickets/${id}/checklist`, { text });
      setNewChecklistText('');
      setData(prev => prev ? { ...prev, checklist: [...(prev.checklist ?? []), {
        ItemId: res.itemId, Text: text, IsDone: false,
        SortOrder: (prev.checklist?.length ?? 0) + 1,
        CheckedByUserId: null, CheckedByName: null, CheckedAt: null,
      }] } : prev);
    } catch (e: any) { alert(e?.message ?? 'Failed to add item'); }
    finally { setChecklistBusy(false); }
  };
  const toggleChecklistItem = async (itemId: number, isDone: boolean) => {
    setChecklistBusy(true);
    try {
      await api.patch(`/api/tickets/${id}/checklist/${itemId}`, { isDone });
      setData(prev => prev ? { ...prev, checklist: (prev.checklist ?? []).map(it =>
        it.ItemId === itemId ? { ...it,
          IsDone: isDone,
          CheckedByUserId: isDone ? me.userId : null,
          CheckedByName: isDone ? me.displayName : null,
          CheckedAt: isDone ? new Date().toISOString() : null,
        } : it) } : prev);
    } catch (e: any) { alert(e?.message ?? 'Failed to update item'); }
    finally { setChecklistBusy(false); }
  };
  const deleteChecklistItem = async (itemId: number) => {
    setChecklistBusy(true);
    try {
      await api.del(`/api/tickets/${id}/checklist/${itemId}`);
      setData(prev => prev ? { ...prev, checklist: (prev.checklist ?? []).filter(it => it.ItemId !== itemId) } : prev);
    } catch (e: any) { alert(e?.message ?? 'Failed to delete item'); }
    finally { setChecklistBusy(false); }
  };

  const addWatcher = async () => {
    if (typeof addWatcherUserId !== 'number') return;
    try {
      await api.post(`/api/tickets/${id}/watchers`, { userId: addWatcherUserId });
      setShowAddWatcher(false);
      setAddWatcherUserId('');
      // Reload just the watchers
      const w = await api.get<typeof watchers>(`/api/tickets/${id}/watchers`);
      setWatchers(w);
    } catch (e: any) { alert(`Failed to add watcher: ${e?.message}`); }
  };

  const removeWatcher = async (watcherId: number, displayName: string) => {
    if (!window.confirm(`Remove ${displayName} as a watcher?`)) return;
    try {
      await api.del(`/api/tickets/${id}/watchers/${watcherId}`);
      const w = await api.get<typeof watchers>(`/api/tickets/${id}/watchers`);
      setWatchers(w);
    } catch (e: any) { alert(`Failed to remove watcher: ${e?.message}`); }
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateField = async (patch: Partial<TicketDetail>) => {
    try {
      // Translate PascalCase UI keys -> camelCase API keys
      const apiBody: any = {};
      if ('AssignedToUserId' in patch) apiBody.assignedToUserId = (patch as any).AssignedToUserId;
      if ('CreatedByUserId' in patch) apiBody.createdByUserId = (patch as any).CreatedByUserId;
      if ('StatusId' in patch) apiBody.statusId = (patch as any).StatusId;
      if ('Priority' in patch) apiBody.priority = (patch as any).Priority;
      if ('Subject' in patch) apiBody.subject = (patch as any).Subject;
      if ('Description' in patch) apiBody.description = (patch as any).Description;
      if ('CategoryId' in patch) apiBody.categoryId = (patch as any).CategoryId;
      if ('Resolution' in patch) apiBody.resolution = (patch as any).Resolution;
      await api.patch(`/api/tickets/${id}`, apiBody);
      await load();
    } catch (e: any) {
      // If the server rejected because resolution was missing, surface a useful message
      const msg = e?.message ?? 'unknown error';
      alert(`Update failed: ${msg}`);
      throw e; // let callers know it failed (e.g. the resolution modal stays open)
    }
  };

  // Atomic update for status changes that need a resolution attached.
  // The backend validates these together so we don't end up with a closed ticket and no resolution.
  const updateStatusWithResolution = async (statusId: number, resolution: string, categoryId?: number | null) => {
    const apiBody: any = { statusId, resolution };
    if (categoryId !== undefined) apiBody.categoryId = categoryId;
    await api.patch(`/api/tickets/${id}`, apiBody);
    await load();
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      await api.post(`/api/tickets/${id}/comments`, {
        body: newComment.trim(),
        isInternal,
      });
      setNewComment('');
      setIsInternal(false);
      await load();
    } catch (e: any) {
      alert(`Failed to post comment: ${e?.message ?? 'unknown error'}`);
    } finally {
      setPostingComment(false);
    }
  };

  // -------- Resolution modal state (intercepts terminal status moves) --------
  const [resModalOpen, setResModalOpen] = useState(false);
  const [pendingStatusId, setPendingStatusId] = useState<number | null>(null);
  const [resolutionText, setResolutionText] = useState('');
  const [resCategoryId, setResCategoryId] = useState<number | null>(null);
  const [savingResolution, setSavingResolution] = useState(false);

  // When the user picks a new status from the dropdown, decide if it needs the
  // resolution modal. Terminal statuses (Resolved, Closed) require it.
  const onStatusChange = (newStatusId: number) => {
    if (!data) return;
    if (newStatusId === data.ticket.StatusId) return;
    const newStatus = statuses.find(s => s.StatusId === newStatusId);
    if (newStatus?.IsTerminal) {
      setPendingStatusId(newStatusId);
      // Pre-fill with existing resolution if any (so re-closing keeps text)
      setResolutionText(data.ticket.Resolution ?? '');
      setResCategoryId(data.ticket.CategoryId ?? null);
      setResModalOpen(true);
    } else {
      // Non-terminal — just update directly
      updateField({ StatusId: newStatusId } as any);
    }
  };

  const submitResolution = async () => {
    if (!pendingStatusId) return;
    if (resolutionText.trim().length < 10) {
      alert('Please enter a resolution of at least 10 characters.');
      return;
    }
    setSavingResolution(true);
    try {
      await updateStatusWithResolution(pendingStatusId, resolutionText.trim(), resCategoryId);
      setResModalOpen(false);
      setPendingStatusId(null);
      setResolutionText('');
    } catch {
      // updateStatusWithResolution alerts on failure
    } finally {
      setSavingResolution(false);
    }
  };

  // -------- Timer state --------
  // The server is the source of truth for whether a timer is running (the
  // Tickets.ActiveTimerStartedAt column). On mount, if the current user is
  // assigned to the ticket AND no timer is active, we auto-start one.
  // We display elapsed seconds locally; only the rounded minute count is sent
  // back on stop.
  const [elapsedSec, setElapsedSec] = useState(0);
  const [startingTimer, setStartingTimer] = useState(false);
  const [stoppingTimer, setStoppingTimer] = useState(false);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualNote, setManualNote] = useState('');
  const autoStartedRef = useRef(false);

  // Recompute elapsed seconds when ActiveTimerStartedAt updates
  useEffect(() => {
    if (!data?.ticket?.ActiveTimerStartedAt) {
      setElapsedSec(0);
      return;
    }
    const started = new Date(data.ticket.ActiveTimerStartedAt).getTime();
    const tick = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [data?.ticket?.ActiveTimerStartedAt]);

  // -------- Stop the timer when the tech leaves the ticket --------
  // The timer auto-starts on open; without this the server clock would keep
  // running after you navigate away (the bug being fixed). We hold an
  // always-fresh closure in a ref and fire it once on unmount. A sub-minute
  // view rounds to 0 minutes, which the server treats as "clear the clock, log
  // nothing" — so brief glances don't create noise, and time is never lost.
  const leaveStopRef = useRef<() => void>(() => {});
  leaveStopRef.current = () => {
    const startedAt = data?.ticket?.ActiveTimerStartedAt;
    if (!startedAt) return;
    const secs = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    const minutes = Math.round(secs / 60); // < 30s → 0 → server clears without an entry
    (async () => {
      try {
        const token = await api.getAccessToken();
        await fetch(`${api.apiBase.replace(/\/$/, '')}/api/tickets/${id}/time/stop`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ minutes }),
          keepalive: true, // let the request survive the navigation / tab close
        });
      } catch { /* best effort — nothing to do if the page is going away */ }
    })();
  };
  useEffect(() => () => { leaveStopRef.current(); }, []);

  // Auto-start: when a tech opens the ticket and no timer is running and
  // they are the assignee, start one. We use a ref to avoid re-firing.
  useEffect(() => {
    if (!data || !canEdit || autoStartedRef.current) return;
    const t = data.ticket;
    if (t.ActiveTimerStartedAt) return; // already running
    if (t.AssignedToUserId !== me.userId) return; // only assignee auto-starts
    autoStartedRef.current = true;
    // Fire-and-forget; the load() afterwards refreshes the page state
    (async () => {
      try {
        await api.post(`/api/tickets/${id}/time/start`, {});
        await load();
      } catch (e) {
        console.warn('Auto-start timer failed:', e);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.ticket?.ActiveTimerStartedAt, data?.ticket?.AssignedToUserId, canEdit]);

  const startTimer = async () => {
    setStartingTimer(true);
    try {
      await api.post(`/api/tickets/${id}/time/start`, {});
      await load();
    } catch (e: any) {
      alert(`Failed to start timer: ${e?.message ?? 'unknown error'}`);
    } finally {
      setStartingTimer(false);
    }
  };

  const stopTimer = async () => {
    if (!data?.ticket?.ActiveTimerStartedAt) return;
    setStoppingTimer(true);
    try {
      const minutes = Math.max(1, Math.round(elapsedSec / 60));
      await api.post(`/api/tickets/${id}/time/stop`, { minutes });
      await load();
    } catch (e: any) {
      alert(`Failed to stop timer: ${e?.message ?? 'unknown error'}`);
    } finally {
      setStoppingTimer(false);
    }
  };

  const submitManualEntry = async () => {
    const minutes = parseInt(manualMinutes, 10);
    if (!minutes || minutes <= 0) {
      alert('Enter a positive number of minutes');
      return;
    }
    try {
      await api.post(`/api/tickets/${id}/time/manual`, {
        minutes,
        note: manualNote.trim() || null,
      });
      setManualEntryOpen(false);
      setManualMinutes('');
      setManualNote('');
      await load();
    } catch (e: any) {
      alert(`Failed to log time: ${e?.message ?? 'unknown error'}`);
    }
  };

  const fmtHM = (totalMin: number) => {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const fmtTimer = (sec: number) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading ticket…</div>;
  if (error) return (
    <div style={{ padding: 40, color: C.danger }}>
      <Link to="/tickets" style={{ color: C.primary, display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 16, textDecoration: 'none' }}>
        <ArrowLeft size={14} /> Back to tickets
      </Link>
      <div>Failed to load ticket: {error}</div>
    </div>
  );
  if (!data) return null;

  const { ticket, comments, tags, sla } = data;
  const assetLinks: any[] = (data as any).assetLinks ?? [];
  const kbLinks: any[] = (data as any).kbLinks ?? [];
  const visibleComments = canSeeInternal ? comments : comments.filter(c => !c.IsInternal);

  return (
    <div>
      {/* Back link */}
      <Link to="/tickets" style={{
        color: C.primary, display: 'inline-flex', alignItems: 'center', gap: 4,
        marginBottom: 12, textDecoration: 'none', fontSize: 13,
      }}>
        <ArrowLeft size={14} /> Back to tickets
      </Link>

      {/* Merged-into banner — appears if THIS ticket was merged into another */}
      {ticket.MergedIntoTicketId && (
        <div style={{
          background: 'var(--c-internal-bg)', border: '1px solid var(--c-internal-border)', borderRadius: 6,
          padding: '10px 14px', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 13, color: '#7a5f1a',
        }}>
          <span style={{ fontSize: 16 }}>⤵</span>
          <span>
            This ticket was merged into{' '}
            <Link to={`/tickets/${ticket.MergedIntoTicketId}`} style={{
              color: '#7a5f1a', fontWeight: 600, textDecoration: 'underline',
            }}>
              {ticket.MergedIntoTicketKey ?? `#${ticket.MergedIntoTicketId}`}
            </Link>
            . New activity should happen there.
          </span>
        </div>
      )}

      {/* Satisfaction survey */}
      {data.survey && (() => {
        const isRequester = me.userId === ticket.CreatedByUserId;
        const responded = !!data.survey.RespondedAt;
        const Stars: React.FC<{ value: number; onPick?: (n: number) => void }> = ({ value, onPick }) => (
          <span style={{ display: 'inline-flex', gap: 2 }}>
            {[1,2,3,4,5].map(n => (
              <span key={n}
                onClick={onPick ? () => onPick(n) : undefined}
                style={{ cursor: onPick ? 'pointer' : 'default', fontSize: 22, lineHeight: 1, color: n <= value ? '#e2a92e' : C.border }}>★</span>
            ))}
          </span>
        );
        if (responded) {
          return (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Satisfaction rating</div>
              <Stars value={data.survey.Rating ?? 0} />
              {data.survey.Comment && (
                <div style={{ fontSize: 13, color: C.text, marginTop: 8, whiteSpace: 'pre-wrap' }}>{data.survey.Comment}</div>
              )}
            </div>
          );
        }
        if (isRequester) {
          return (
            <div style={{ background: 'var(--c-resolution-bg)', border: `1px solid ${C.border}`, borderRadius: 6, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>How did we do?</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>Your ticket was resolved — please rate your support.</div>
              <Stars value={ratingValue} onPick={setRatingValue} />
              <textarea
                value={ratingComment}
                onChange={e => setRatingComment(e.target.value)}
                placeholder="Add a comment (optional)"
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, minHeight: 60, padding: 8, borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, fontFamily: 'inherit' }}
              />
              <div style={{ marginTop: 8 }}>
                <button onClick={submitRating} disabled={ratingValue < 1 || ratingBusy}
                  style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: ratingValue < 1 || ratingBusy ? 'default' : 'pointer', opacity: ratingValue < 1 || ratingBusy ? 0.6 : 1 }}>
                  {ratingBusy ? 'Submitting…' : 'Submit rating'}
                </button>
              </div>
            </div>
          );
        }
        return (
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 14 }}>
            Awaiting the requester's satisfaction rating.
          </div>
        );
      })()}

      {/* Header */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontFamily: 'monospace', color: C.textMuted, fontSize: 14 }}>
              {ticket.TicketKey ?? `#${ticket.TicketId}`}
            </span>
            <Badge color={PRIORITY_COLORS[ticket.Priority] ?? C.secondary}>{ticket.Priority}</Badge>
            <Badge color={C.secondary}>{ticket.StatusName}</Badge>
            {ticket.CategoryName && <Badge color={C.textMuted}>{ticket.CategoryName}</Badge>}
          </div>
          {editingTitle ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 4px' }}>
              <input
                autoFocus
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                disabled={savingTitle}
                style={{
                  flex: 1, fontSize: 22, fontWeight: 600, color: C.text, background: C.surface,
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px',
                }}
              />
              <button onClick={saveTitle} disabled={savingTitle} aria-label="Save title" title="Save"
                style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 9px', cursor: 'pointer', lineHeight: 0 }}>
                <Check size={16} />
              </button>
              <button onClick={() => setEditingTitle(false)} disabled={savingTitle} aria-label="Cancel" title="Cancel"
                style={{ background: 'transparent', color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 9px', cursor: 'pointer', lineHeight: 0 }}>
                <X size={16} />
              </button>
            </div>
          ) : (
            <h1 style={{ color: C.text, fontSize: 22, margin: '0 0 4px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              {ticket.Subject}
              {canEdit && (
                <button
                  onClick={() => { setTitleDraft(ticket.Subject); setEditingTitle(true); }}
                  aria-label="Edit title" title="Edit title"
                  style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, lineHeight: 0 }}>
                  <Pencil size={15} />
                </button>
              )}
            </h1>
          )}
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Opened by <UserAccessPopover userId={ticket.CreatedByUserId} name={ticket.CreatedByName} /> · {fmtDate(ticket.CreatedAt)}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 280px', gap: 20 }}>
        {/* Main column */}
        <div>
          {/* Description */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Description</h3>
              {canEdit && !editingDesc && (
                <button onClick={() => { setDescDraft(ticket.Description ?? ''); setEditingDesc(true); }}
                  title="Edit description"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <Pencil size={14} /> Edit
                </button>
              )}
            </div>
            {editingDesc ? (
              <div>
                <textarea value={descDraft} onChange={e => setDescDraft(e.target.value)} rows={6} autoFocus
                  style={{ width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 14, lineHeight: 1.5, borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontFamily: 'inherit', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={saveDesc} disabled={savingDesc}
                    style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: savingDesc ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Check size={14} /> {savingDesc ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEditingDesc(false)} disabled={savingDesc}
                    style={{ background: 'transparent', color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 4, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <DescriptionBody text={ticket.Description} />
            )}
          </Card>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <Card>
              <h3 style={{ margin: '0 0 10px', fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Tags</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tags.map(t => <Badge key={t} color={C.secondary}>{t}</Badge>)}
              </div>
            </Card>
          )}

          {/* Checklist — internal steps, checked off by admin/tech */}
          {canSeeInternal && (() => {
            const items = data.checklist ?? [];
            const done = items.filter(i => i.IsDone).length;
            return (
              <Card>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  Checklist {items.length > 0 && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>· {done}/{items.length} done</span>}
                </h3>
                {items.length === 0 && !canEdit && (
                  <div style={{ fontSize: 13, color: C.textMuted }}>No checklist items.</div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map(item => (
                    <div key={item.ItemId} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={item.IsDone}
                        disabled={!canEdit || checklistBusy}
                        onChange={e => toggleChecklistItem(item.ItemId, e.target.checked)}
                        style={{ marginTop: 3, cursor: canEdit ? 'pointer' : 'default', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: C.text, textDecoration: item.IsDone ? 'line-through' : 'none', opacity: item.IsDone ? 0.7 : 1 }}>
                          {item.Text}
                        </div>
                        {item.IsDone && item.CheckedByName && (
                          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                            ✓ {item.CheckedByName}{item.CheckedAt ? ` · ${fmtDate(item.CheckedAt)}` : ''}
                          </div>
                        )}
                      </div>
                      {canEdit && (
                        <button onClick={() => deleteChecklistItem(item.ItemId)} disabled={checklistBusy}
                          title="Delete item" style={{ background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 8, marginTop: items.length > 0 ? 12 : 0 }}>
                    <input
                      value={newChecklistText}
                      onChange={e => setNewChecklistText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addChecklistItem(); }}
                      placeholder="Add a step…"
                      style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 4, padding: '7px 10px', fontSize: 13, background: C.surface, color: C.text }}
                    />
                    <button onClick={addChecklistItem} disabled={checklistBusy || !newChecklistText.trim()}
                      style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '7px 14px', fontSize: 13, cursor: checklistBusy || !newChecklistText.trim() ? 'default' : 'pointer', opacity: checklistBusy || !newChecklistText.trim() ? 0.6 : 1, whiteSpace: 'nowrap' }}>Add</button>
                  </div>
                )}
              </Card>
            );
          })()}

          {/* Resolution (shown when present) */}
          {ticket.Resolution && (
            <Card style={{ borderLeft: `3px solid ${C.ok}`, background: 'var(--c-resolution-bg)' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Resolution
              </h3>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5, color: C.text }}>
                {ticket.Resolution}
              </div>
            </Card>
          )}

          {/* Attachments */}
          <TicketAttachments
            ticketId={id!}
            canUpload={me.role !== 'viewer'}
            canMarkInternal={canEdit}
          />

          {/* Comments */}
          <Card>
            <h3 style={{ margin: '0 0 14px', fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={14} /> Activity ({visibleComments.length})
            </h3>

            {visibleComments.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
                No comments yet. Be the first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {visibleComments.map(c => (
                  <div key={c.CommentId} style={{
                    padding: 12,
                    background: c.IsInternal ? 'var(--c-internal-bg)' : C.bg,
                    border: c.IsInternal ? '1px solid var(--c-internal-border)' : `1px solid ${C.border}`,
                    borderRadius: 6,
                    opacity: c.IsDeleted ? 0.6 : 1,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {c.AuthorName}
                        {c.IsInternal && <Badge color={C.warn} >Internal</Badge>}
                        {c.EditedAt && !c.IsDeleted && (
                          <span style={{ fontSize: 11, fontWeight: 400, fontStyle: 'italic', color: C.textMuted }}>(edited)</span>
                        )}
                        {c.MinutesLogged && c.MinutesLogged > 0 && (
                          <span title="Time logged on this reply" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: 11, fontWeight: 500,
                            color: C.primary, background: '#eaf2ec',
                            padding: '2px 7px', borderRadius: 10,
                          }}>
                            <Clock size={10} /> {fmtHM(c.MinutesLogged)}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {!c.IsDeleted && editingCommentId !== c.CommentId && c.AuthorUserId === me.userId && (
                          <button onClick={() => startEditComment(c)} title="Edit" aria-label="Edit comment"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 2, lineHeight: 0 }}>
                            <Pencil size={13} />
                          </button>
                        )}
                        {!c.IsDeleted && canEdit && (
                          <button onClick={() => deleteComment(c.CommentId)} disabled={commentBusy} title="Delete" aria-label="Delete comment"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 2, lineHeight: 0 }}>
                            <Trash2 size={13} />
                          </button>
                        )}
                        <div style={{ fontSize: 11, color: C.textMuted }}>{fmtDate(c.CreatedAt)}</div>
                      </div>
                    </div>
                    {c.IsDeleted ? (
                      <div style={{ fontSize: 13, fontStyle: 'italic', color: C.textMuted }}>[deleted]</div>
                    ) : editingCommentId === c.CommentId ? (
                      <div>
                        <textarea
                          value={commentDraft}
                          onChange={e => setCommentDraft(e.target.value)}
                          rows={3}
                          style={{ width: '100%', fontSize: 13, padding: 8, borderRadius: 6,
                                   border: `1px solid ${C.border}`, background: C.surface, color: C.text, boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <button onClick={() => saveEditComment(c.CommentId)} disabled={commentBusy}
                            style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Check size={13} /> Save
                          </button>
                          <button onClick={() => { setEditingCommentId(null); setCommentDraft(''); }} disabled={commentBusy}
                            style={{ background: 'transparent', color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <DescriptionBody text={c.Body} style={{ fontSize: 13, lineHeight: 1.5 }} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Composer */}
            {me.role !== 'viewer' && (
              <div style={{ marginTop: 18, padding: 12, border: `1px solid ${C.border}`, borderRadius: 6, background: C.bg }}>
                <textarea
                  ref={commentRef}
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  onPaste={onCommentPaste}
                  placeholder="Add a comment… you can paste images directly"
                  rows={3}
                  style={{
                    width: '100%', boxSizing: 'border-box', border: `1px solid ${C.border}`,
                    padding: 10, borderRadius: 4, fontSize: 13, fontFamily: 'inherit',
                    resize: 'vertical', background: C.surface,
                  }}
                />
                {commentImgUploading && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Uploading image…</div>}
                {commentImgError && <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>{commentImgError}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  {canEdit ? (
                    <label style={{ fontSize: 12, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                      Internal note (only techs and admins see this)
                    </label>
                  ) : <div />}
                  <button
                    onClick={postComment}
                    disabled={!newComment.trim() || postingComment}
                    style={{
                      background: !newComment.trim() || postingComment ? C.border : C.primary,
                      color: '#fff', border: 'none', padding: '6px 14px',
                      borderRadius: 6, fontSize: 13, fontWeight: 600,
                      cursor: !newComment.trim() || postingComment ? 'not-allowed' : 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <Send size={12} /> {postingComment ? 'Posting…' : 'Post'}
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Right sidebar */}
        <div>
          {/* SLA */}
          {sla && (sla.responseDueAt || sla.resolveDueAt) && (
            <Card>
              <h3 style={{ margin: '0 0 10px', fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>SLA</h3>
              {sla.responseDueAt && <SLABar pct={sla.responseElapsedPct} label="Response" done={sla.responseDone} met={sla.responseMet} />}
              {sla.resolveDueAt && <SLABar pct={sla.resolveElapsedPct} label="Resolution" done={sla.resolveDone} met={sla.resolveMet} />}
            </Card>
          )}

          {canEdit && (
            <Card>
              <h3 style={{ margin: '0 0 10px', fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={12} /> Time Tracking
              </h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: C.textMuted }}>Total logged</span>
                <span style={{ fontSize: 16, fontWeight: 600, color: C.text }}>{fmtHM(ticket.TotalTimeMinutes ?? 0)}</span>
              </div>
              {ticket.ActiveTimerStartedAt ? (
                <>
                  <div style={{
                    background: '#faf7f2', border: `1px solid ${C.border}`, borderRadius: 6,
                    padding: '10px 12px', marginBottom: 8,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 11, color: C.textMuted }}>Running</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600, color: C.primary }}>
                      {fmtTimer(elapsedSec)}
                    </span>
                  </div>
                  <button
                    onClick={stopTimer}
                    disabled={stoppingTimer}
                    style={btnSty('danger')}
                  >
                    <Square size={12} /> {stoppingTimer ? 'Stopping…' : 'Stop timer'}
                  </button>
                </>
              ) : (
                <button
                  onClick={startTimer}
                  disabled={startingTimer}
                  style={btnSty('primary')}
                >
                  <Play size={12} /> {startingTimer ? 'Starting…' : 'Start timer'}
                </button>
              )}
              <button
                onClick={() => setManualEntryOpen(true)}
                style={{ ...btnSty('ghost'), marginTop: 8 }}
              >
                <Plus size={12} /> Log time manually
              </button>
            </Card>
          )}

          {/* Watchers (secondary contacts) — visible to anyone with access to the ticket.
              Add/remove gated to admin/tech (server also enforces). Users see only the list. */}
          <Card>
            <h3 style={{
              margin: '0 0 10px', fontSize: 12, color: C.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>
              Also notified
            </h3>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8 }}>
              Primary: <strong style={{ color: C.text }}>{ticket.CreatedByName}</strong>
            </div>
            {watchers.length === 0 ? (
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8, fontStyle: 'italic' }}>
                No additional contacts.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                {watchers.map(w => (
                  <div
                    key={w.WatcherId}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '4px 8px', borderRadius: 4,
                      background: C.bg, border: `1px solid ${C.border}`,
                      fontSize: 12,
                    }}
                  >
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ color: C.text, fontWeight: 500 }}><UserAccessPopover userId={w.UserId} name={w.DisplayName} /></div>
                      <div style={{ fontSize: 10, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.Email}</div>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => removeWatcher(w.WatcherId, w.DisplayName)}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: C.textMuted, padding: 2, marginLeft: 4,
                        }}
                        title="Remove"
                      ><X size={12} /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {canEdit && !showAddWatcher && (
              <button onClick={() => setShowAddWatcher(true)} style={btnSty('ghost')}>
                <Plus size={12} /> Add contact
              </button>
            )}
            {canEdit && showAddWatcher && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <SearchableSelect
                  options={users
                    .filter(u => u.UserId !== ticket.CreatedByUserId)
                    .filter(u => !watchers.find(w => w.UserId === u.UserId))
                    .sort((a, b) => a.DisplayName.localeCompare(b.DisplayName))
                    .map(u => ({ value: u.UserId, label: `${u.DisplayName} — ${u.Email}` }))}
                  value={addWatcherUserId}
                  onChange={v => setAddWatcherUserId(v === '' ? '' : Number(v))}
                  placeholder="Search people…"
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={addWatcher}
                    disabled={typeof addWatcherUserId !== 'number'}
                    style={{
                      background: typeof addWatcherUserId === 'number' ? C.primary : '#ccc',
                      color: 'white', border: 'none', padding: '6px 12px',
                      borderRadius: 4, fontSize: 12, fontWeight: 600,
                      cursor: typeof addWatcherUserId === 'number' ? 'pointer' : 'not-allowed',
                      flex: 1,
                    }}
                  >Add</button>
                  <button
                    onClick={() => { setShowAddWatcher(false); setAddWatcherUserId(''); }}
                    style={{
                      background: 'transparent', border: `1px solid ${C.border}`,
                      padding: '6px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                    }}
                  >Cancel</button>
                </div>
              </div>
            )}
          </Card>

          {/* Merge — admin/tech only. Hidden if this ticket has itself been merged into another. */}
          {canEdit && !ticket.MergedIntoTicketId && (
            <Card>
              <h3 style={{
                margin: '0 0 10px', fontSize: 12, color: C.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.4px',
              }}>
                Merge
              </h3>
              {data.mergedChildren && data.mergedChildren.length > 0 ? (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>
                    Merged into this ticket:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {data.mergedChildren.map(c => (
                      <div key={c.TicketId} style={{
                        display: 'flex', flexDirection: 'column', gap: 6,
                        padding: '6px 8px', borderRadius: 4,
                        background: C.bg, border: `1px solid ${C.border}`,
                      }}>
                        <Link
                          to={`/tickets/${c.TicketId}`}
                          style={{ fontSize: 12, color: C.text, textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}
                          title={c.Subject}
                        >
                          <span style={{ fontFamily: 'monospace', color: C.primary, fontSize: 11 }}>
                            {c.TicketKey}
                          </span>
                          <span style={{
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', color: C.textMuted, fontSize: 11,
                          }}>{c.Subject}</span>
                        </Link>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <select
                            value={unmergeStatus[c.TicketId] ?? (statuses.find(s => s.Name === 'Open') ?? statuses.find(s => !s.IsTerminal) ?? statuses[0])?.StatusId ?? 0}
                            onChange={e => setUnmergeStatus(prev => ({ ...prev, [c.TicketId]: Number(e.target.value) }))}
                            style={{ flex: 1, fontSize: 11, padding: '3px 4px', borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface, color: C.text }}
                          >
                            {statuses.map(s => <option key={s.StatusId} value={s.StatusId}>{s.Name}</option>)}
                          </select>
                          <button onClick={() => doUnmerge(c.TicketId)} disabled={unmergeBusy === c.TicketId}
                            style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface, color: C.text, cursor: 'pointer' }}>
                            {unmergeBusy === c.TicketId ? '…' : 'Unmerge'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 10 }}>
                  Find duplicate tickets and merge them into this one. They'll be closed and their conversation history stays accessible.
                </div>
              )}
              <button
                onClick={() => setMergeModalOpen(true)}
                style={btnSty('ghost')}
              >
                <GitMerge size={12} /> Merge tickets in
              </button>
            </Card>
          )}

          {/* Linked devices */}
          <Card>
            <h3 style={{ margin: '0 0 10px', fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Link2 size={13} /> Linked devices
            </h3>
            {assetLinks.length === 0 ? (
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: canEdit ? 10 : 0 }}>No devices linked.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: canEdit ? 10 : 0 }}>
                {assetLinks.map((a: any) => (
                  <div key={a.AssetId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                    <span>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.textMuted }}>{a.AssetTag}</span>{' '}{a.Name}
                    </span>
                    {canEdit && (
                      <button onClick={() => unlinkAsset(a.AssetId)} disabled={assetBusy} title="Unlink" aria-label="Unlink device"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 2, lineHeight: 0 }}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {canEdit && (
              <div style={{ display: 'flex', gap: 6 }}>
                <SearchableSelect
                  style={{ flex: 1 }}
                  options={allAssets
                    .filter(a => !assetLinks.some(l => l.AssetId === a.AssetId))
                    .map(a => ({ value: a.AssetId, label: `${a.AssetTag} — ${a.Name}` }))}
                  value={assetPickerId}
                  onOpen={loadAssetsForPicker}
                  onChange={v => setAssetPickerId(v === '' ? '' : Number(v))}
                  placeholder="Search devices…"
                  emptyLabel="Link a device…"
                />
                <button onClick={linkAsset} disabled={!assetPickerId || assetBusy}
                  style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '0 12px', cursor: 'pointer', fontSize: 13 }}>
                  Link
                </button>
              </div>
            )}
          </Card>

          {canEdit && (
          <Card>
            <h3 style={{ margin: '0 0 4px', fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Link2 size={13} /> Linked KB articles
            </h3>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 10 }}>Visible to techs &amp; admins only.</div>
            {kbLinks.length === 0 ? (
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>No articles tagged.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                {kbLinks.map((a: any) => (
                  <div key={a.ArticleId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                    <span>
                      <a href={`/kb?article=${a.ArticleId}`} target="_blank" rel="noopener noreferrer"
                        style={{ color: C.primary, textDecoration: 'none' }}>
                        {a.Title}
                      </a>
                      {a.Visibility === 'internal' && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: C.warn, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Internal</span>
                      )}
                    </span>
                    <button onClick={() => unlinkKb(a.ArticleId)} disabled={kbBusy} title="Untag" aria-label="Untag article"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 2, lineHeight: 0 }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <SearchableSelect
                style={{ flex: 1 }}
                options={allKbArticles
                  .filter(a => !kbLinks.some(l => l.ArticleId === a.ArticleId))
                  .map(a => ({ value: a.ArticleId, label: a.Visibility === 'internal' ? `${a.Title} (internal)` : a.Title }))}
                value={kbPickerId}
                onOpen={loadKbForPicker}
                onChange={v => setKbPickerId(v === '' ? '' : Number(v))}
                placeholder="Search articles…"
                emptyLabel="Tag an article…"
              />
              <button onClick={linkKb} disabled={!kbPickerId || kbBusy}
                style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '0 12px', cursor: 'pointer', fontSize: 13 }}>
                Tag
              </button>
            </div>
          </Card>
          )}

          <Card>
            <h3 style={{ margin: '0 0 12px', fontSize: 12, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Details</h3>

            <SidebarField
              label="Requester"
              value={
                canEdit ? (
                  <SearchableSelect
                    options={users.map(u => ({ value: u.UserId, label: u.DisplayName }))}
                    value={ticket.CreatedByUserId}
                    onChange={v => {
                      const newId = Number(v);
                      if (v !== '' && newId !== ticket.CreatedByUserId) {
                        if (window.confirm('Change the requester on this ticket? The new requester will be notified by email.')) {
                          updateField({ CreatedByUserId: newId } as any);
                        }
                      }
                    }}
                    placeholder="Search people…"
                  />
                ) : <UserAccessPopover userId={ticket.CreatedByUserId} name={ticket.CreatedByName} />
              }
            />
            <SidebarField
              label="Assignee"
              value={
                canEdit ? (
                  <>
                  <SearchableSelect
                    options={users.filter(u => u.Role === 'admin' || u.Role === 'tech' || u.Role === 'agent').map(u => ({ value: u.UserId, label: u.DisplayName }))}
                    value={ticket.AssignedToUserId ?? ''}
                    onChange={v => updateField({ AssignedToUserId: v === '' ? null : Number(v) })}
                    emptyLabel="Unassigned"
                    placeholder="Search people…"
                  />
                  {me.userId !== ticket.AssignedToUserId && (
                    <button
                      onClick={() => updateField({ AssignedToUserId: me.userId })}
                      style={{ marginTop: 6, display: 'block', background: 'transparent',
                        border: `1px solid ${C.primary}`, color: C.primary, borderRadius: 4,
                        padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                    >Assign to me</button>
                  )}
                  </>
                ) : (ticket.AssignedToName != null
                  ? <UserAccessPopover userId={ticket.AssignedToUserId!} name={ticket.AssignedToName} />
                  : <span style={{ color: C.textMuted, fontStyle: 'italic' }}>Unassigned</span>)
              }
            />

            <SidebarField
              label="Also working"
              value={
                <>
                  {(ticket.SecondaryAssignees ?? []).map(sa => (
                    <div key={sa.UserId} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <UserAccessPopover userId={sa.UserId} name={sa.DisplayName} />
                      {(me.role === 'admin' || me.role === 'tech') && (
                        <button
                          title="Remove"
                          onClick={async () => {
                            try { await api.del(`/api/tickets/${ticket.TicketId}/assignees/${sa.UserId}`); await load(); }
                            catch (e: any) { alert(e?.message ?? 'Could not remove'); }
                          }}
                          style={{ background: 'transparent', border: 'none', color: C.textMuted,
                            cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                        >×</button>
                      )}
                    </div>
                  ))}
                  {(me.role === 'admin' || me.role === 'tech') && (ticket.SecondaryAssignees ?? []).length < 2 ? (
                    <SearchableSelect
                      options={users
                        .filter(u => (u.Role === 'admin' || u.Role === 'tech' || u.Role === 'agent')
                          && u.UserId !== ticket.AssignedToUserId
                          && !(ticket.SecondaryAssignees ?? []).some(sa => sa.UserId === u.UserId))
                        .map(u => ({ value: u.UserId, label: u.DisplayName }))}
                      value={''}
                      onChange={async v => {
                        if (v === '') return;
                        try { await api.post(`/api/tickets/${ticket.TicketId}/assignees`, { userId: Number(v) }); await load(); }
                        catch (e: any) { alert(e?.message ?? 'Could not add'); }
                      }}
                      emptyLabel="Add person…"
                      placeholder="Search people…"
                    />
                  ) : ((ticket.SecondaryAssignees ?? []).length === 0
                    ? <span style={{ color: C.textMuted, fontStyle: 'italic' }}>None</span>
                    : null)}
                </>
              }
            />

            <SidebarField
              label="Status"
              value={
                canEdit ? (
                  <select
                    value={ticket.StatusId}
                    onChange={e => onStatusChange(parseInt(e.target.value))}
                    style={selectSty()}
                  >
                    {statuses.map(s => (
                      <option key={s.StatusId} value={s.StatusId}>{s.Name}</option>
                    ))}
                  </select>
                ) : ticket.StatusName
              }
            />

            <SidebarField
              label="Priority"
              value={
                canEdit ? (
                  <select
                    value={ticket.Priority}
                    onChange={e => updateField({ Priority: e.target.value as any })}
                    style={selectSty()}
                  >
                    {['Critical', 'High', 'Medium', 'Low'].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                ) : <Badge color={PRIORITY_COLORS[ticket.Priority] ?? C.secondary}>{ticket.Priority}</Badge>
              }
            />

            {ticket.CategoryName && <SidebarField label="Category" value={ticket.CategoryName} />}

            <SidebarField label="Created" value={fmtDate(ticket.CreatedAt)} />
            {ticket.FirstResponseAt && <SidebarField label="First Response" value={fmtDate(ticket.FirstResponseAt)} />}
            {ticket.ResolvedAt && <SidebarField label="Resolved" value={fmtDate(ticket.ResolvedAt)} />}
          </Card>
        </div>
      </div>

      {/* Resolution required modal */}
      {resModalOpen && (
        <Modal onClose={() => { if (!savingResolution) { setResModalOpen(false); setPendingStatusId(null); } }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18, color: C.text }}>Resolution required</h2>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: C.textMuted }}>
            Closing or resolving a ticket requires a brief explanation of how it was resolved. This helps with reporting and gives the requester context. (Min 10 characters.)
          </p>
          <textarea
            value={resolutionText}
            onChange={e => setResolutionText(e.target.value)}
            placeholder="e.g. Reset the user's password via Entra ID, confirmed login works."
            rows={5}
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: 10, fontSize: 14, lineHeight: 1.5,
              border: `1px solid ${C.border}`, borderRadius: 4,
              fontFamily: 'inherit', resize: 'vertical',
            }}
          />
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
            {resolutionText.trim().length}/10 minimum
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 }}>Category</label>
            <select
              value={resCategoryId ?? ''}
              onChange={e => setResCategoryId(e.target.value ? parseInt(e.target.value) : null)}
              style={{
                width: '100%', boxSizing: 'border-box', padding: 9, fontSize: 14,
                border: `1px solid ${C.border}`, borderRadius: 4,
                background: C.surface, color: C.text, fontFamily: 'inherit',
              }}
            >
              <option value="">— Uncategorized —</option>
              {categories.map(c => <option key={c.CategoryId} value={c.CategoryId}>{c.Name}</option>)}
            </select>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              Confirm the category before closing — keeps reporting accurate.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setResModalOpen(false); setPendingStatusId(null); }}
              disabled={savingResolution}
              style={{ ...btnSty('ghost'), width: 'auto', padding: '8px 16px' }}
            >Cancel</button>
            <button
              onClick={submitResolution}
              disabled={savingResolution || resolutionText.trim().length < 10}
              style={{ ...btnSty('primary'), width: 'auto', padding: '8px 16px',
                opacity: (savingResolution || resolutionText.trim().length < 10) ? 0.5 : 1 }}
            >{savingResolution ? 'Saving…' : 'Save & close ticket'}</button>
          </div>
        </Modal>
      )}

      {/* Manual time entry modal */}
      {manualEntryOpen && (
        <Modal onClose={() => setManualEntryOpen(false)}>
          <h2 style={{ margin: '0 0 14px', fontSize: 18, color: C.text }}>Log time manually</h2>
          <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 }}>Minutes</label>
          <input
            type="number"
            min={1}
            value={manualMinutes}
            onChange={e => setManualMinutes(e.target.value)}
            placeholder="30"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 10px', fontSize: 14,
              border: `1px solid ${C.border}`, borderRadius: 4,
              marginBottom: 12,
            }}
          />
          <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 }}>Note (optional)</label>
          <input
            type="text"
            value={manualNote}
            onChange={e => setManualNote(e.target.value)}
            placeholder="What did you work on?"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 10px', fontSize: 14,
              border: `1px solid ${C.border}`, borderRadius: 4,
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
            <button onClick={() => setManualEntryOpen(false)}
              style={{ ...btnSty('ghost'), width: 'auto', padding: '8px 16px' }}>Cancel</button>
            <button onClick={submitManualEntry}
              style={{ ...btnSty('primary'), width: 'auto', padding: '8px 16px' }}>Log time</button>
          </div>
        </Modal>
      )}
      {/* Merge modal */}
      {mergeModalOpen && (
        <MergeModal
          parentTicketId={ticket.TicketId}
          parentTicketKey={ticket.TicketKey ?? `#${ticket.TicketId}`}
          onClose={() => setMergeModalOpen(false)}
          onMerged={() => load()}
        />
      )}
    </div>
  );
};

const Modal: React.FC<{ children: React.ReactNode; onClose: () => void }> = ({ children, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: C.surface, borderRadius: 8, padding: 24,
        width: '90%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}
    >
      {children}
    </div>
  </div>
);

const SidebarField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 13, color: C.text }}>{value}</div>
  </div>
);

const selectSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '4px 8px', borderRadius: 4,
  fontSize: 13, width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text,
});

const btnSty = (variant: 'primary' | 'danger' | 'ghost'): React.CSSProperties => {
  const base: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    width: '100%', padding: '8px 12px', borderRadius: 4,
    fontSize: 13, fontWeight: 500, cursor: 'pointer',
    border: 'none', transition: 'opacity 0.15s',
  };
  if (variant === 'primary') return { ...base, background: C.primary, color: 'white' };
  if (variant === 'danger')  return { ...base, background: C.danger, color: 'white' };
  return { ...base, background: 'transparent', color: C.text, border: `1px solid ${C.border}` };
};


// ============================================================================
// MergeModal — search & select tickets to merge INTO the current ticket.
// They get closed; their content stays accessible via the merged-into link.
// ============================================================================
interface MergeCandidate {
  TicketId: number;
  TicketKey: string;
  Subject: string;
  StatusName: string;
  Priority: string;
  CreatedByName: string;
  CreatedAt: string;
}

const MergeModal: React.FC<{
  parentTicketId: number;
  parentTicketKey: string;
  onClose: () => void;
  onMerged: () => void;
}> = ({ parentTicketId, parentTicketKey, onClose, onMerged }) => {
  const api = useApi();
  const [allTickets, setAllTickets] = useState<MergeCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<MergeCandidate[]>('/api/tickets');
        setAllTickets(Array.isArray(data) ? data : []);
      } catch (e: any) {
        alert(`Failed to load tickets: ${e?.message}`);
      } finally { setLoading(false); }
    })();
  }, [api]);

  // Eligible candidates: not the parent itself, not already in a closed/resolved state
  const candidates = React.useMemo(() => {
    const s = search.trim().toLowerCase();
    return allTickets
      .filter(t => t.TicketId !== parentTicketId)
      .filter(t => t.StatusName !== 'Closed')  // keep already-closed out by default; admin can extend if needed
      .filter(t => {
        if (!s) return true;
        return (
          t.Subject.toLowerCase().includes(s) ||
          t.TicketKey.toLowerCase().includes(s) ||
          (t.CreatedByName ?? '').toLowerCase().includes(s)
        );
      })
      .slice(0, 100);  // cap render
  }, [allTickets, search, parentTicketId]);

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const doMerge = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(
      `Merge ${selected.size} ticket(s) into ${parentTicketKey}?\n\nThe selected tickets will be CLOSED and a record will be added to ${parentTicketKey}. This cannot be easily undone.`
    )) return;
    setMerging(true);
    try {
      const result = await api.post<{ ok: boolean; merged: number }>(
        `/api/tickets/${parentTicketId}/merge`,
        { childIds: Array.from(selected) }
      );
      onMerged();
      alert(`Merged ${result.merged} ticket(s) into ${parentTicketKey}.`);
      onClose();
    } catch (e: any) {
      alert(`Merge failed: ${e?.message}`);
    } finally { setMerging(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.surface, borderRadius: 8, width: '100%', maxWidth: 720,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ margin: 0, color: C.primary, fontSize: 16 }}>
              Merge tickets into {parentTicketKey}
            </h3>
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
              Selected tickets will be closed and pointed at this one.
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.textMuted, padding: 4,
          }}><X size={18} /></button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}` }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by subject, ticket #, or requester…"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 12px', fontSize: 13,
              border: `1px solid ${C.border}`, borderRadius: 4,
              background: C.bg,
            }}
          />
          {selected.size > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: C.primary, fontWeight: 600 }}>
              {selected.size} selected
            </div>
          )}
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: C.textMuted }}>Loading…</div>
          ) : candidates.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
              {search.trim() ? 'No matching open tickets.' : 'No other open tickets to merge.'}
            </div>
          ) : (
            candidates.map(t => {
              const isSelected = selected.has(t.TicketId);
              return (
                <div
                  key={t.TicketId}
                  onClick={() => toggle(t.TicketId)}
                  style={{
                    padding: '10px 20px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: 'pointer',
                    background: isSelected ? '#eaf2ec' : 'transparent',
                    borderLeft: isSelected ? `3px solid ${C.primary}` : '3px solid transparent',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = C.bg; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(t.TicketId)}
                    onClick={e => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{
                        fontFamily: 'monospace', fontSize: 11,
                        color: C.primary, fontWeight: 600,
                      }}>{t.TicketKey}</span>
                      <Badge color={PRIORITY_COLORS[t.Priority] ?? C.secondary}>{t.Priority}</Badge>
                      <Badge color={C.textMuted}>{t.StatusName}</Badge>
                    </div>
                    <div style={{
                      fontSize: 13, color: C.text, fontWeight: 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {t.Subject}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                      by {t.CreatedByName} · {new Date(t.CreatedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button onClick={onClose} disabled={merging} style={{
            background: 'transparent', border: `1px solid ${C.border}`,
            padding: '8px 16px', borderRadius: 4, fontSize: 13, cursor: 'pointer',
          }}>Cancel</button>
          <button
            onClick={doMerge}
            disabled={selected.size === 0 || merging}
            style={{
              background: selected.size === 0 ? '#ccc' : C.primary, color: 'white',
              border: 'none', padding: '8px 18px', borderRadius: 4,
              fontSize: 13, fontWeight: 600, cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <GitMerge size={12} />
            {merging ? 'Merging…' : `Merge ${selected.size > 0 ? `${selected.size} ` : ''}into ${parentTicketKey}`}
          </button>
        </div>
      </div>
    </div>
  );
};
